import React, { ChangeEvent, FC, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers'
import { ISelectOption } from 'components/forms/Selects'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import * as yup from 'yup'
import { CarrierSelect } from 'apps/fleet-planner/components/atoms'
import {
  AddEntityForm,
  AddEntityFormFooter,
} from 'apps/fleet-planner/components/forms/AddEntityForm'
import { TrailerSchema, TrailerSchemaType } from 'apps/fleet-planner/schema/TrailerSchema'
import { ICarrier } from 'apps/fleet-planner/types'
import { selectCarrierOptionList, selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { TextInput } from 'components/atoms'
import { Button } from 'transfix-ui'

const emptyTrailerState: TrailerSchemaType = {
  trailerNumber: '',
  motorCarrierId: 0,
}

/**
 * Canonical type for all "Add Entity" form wrappers
 */
export interface IAddNewEntityFormProps<T extends object> {
  /**
   * Sends back successful response data to
   * parent component
   */
  onSuccess: (data: T) => void
  /**
   * When form is canceled or closed
   */
  onCancel?: () => void
  defaultValues?: Partial<T>
  schema?: yup.ObjectSchema<T>
  isLoading?: boolean
  /**
   * Text for "Save changes" button
   * @default 'Save'
   */
  primaryButtonText?: string
  /**
   * Prop to disable input fields, using key names
   * @example 'firstName'`
   */
  disabledFields?: (keyof T)[]
}

export interface ITrailerFormProps extends IAddNewEntityFormProps<TrailerSchemaType> {}

export const TrailerForm: FC<ITrailerFormProps> = ({
  onSuccess: onSuccessProp,
  onCancel: onCancelProp = () => {},
  defaultValues: defaultValuesProp = {},
  isLoading: isLoadingProp = false,
  primaryButtonText: primaryButtonTextProp = 'Add Trailer',
  disabledFields = [],
}) => {
  const carriersOptions = useSelector(selectCarrierOptionList)
  const carriers = useSelector(selectCarriersList)

  const getDefaultMotorCarrier = (): ICarrier | undefined => {
    let carrier: ICarrier | undefined

    if (defaultValuesProp?.motorCarrierId) {
      carrier = carriers.find(c => c.id === defaultValuesProp.motorCarrierId)
    } else if (carriers?.length === 1) {
      const [firstCarrier] = carriers || []
      carrier = firstCarrier
    }

    return carrier
  }

  const motorCarrier = getDefaultMotorCarrier()
  const { motorCarrierId } = motorCarrier || {}

  const { control, errors, handleSubmit, setValue } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(TrailerSchema),
    defaultValues: TrailerSchema.cast({
      ...emptyTrailerState,
      ...defaultValuesProp,
      motorCarrier,
      motorCarrierId,
    }),
  })

  /**
   * Update if this value is set async for some reason
   */
  useEffect(() => {
    const matchedCarrier = carriers.find(c => c.id === defaultValuesProp?.motorCarrierId)
    if (matchedCarrier) {
      setValue('motorCarrierId', matchedCarrier?.id)
      setValue('motorCarrier', matchedCarrier)
    }
  }, [carriers, defaultValuesProp.motorCarrierId, setValue])

  const submitHandler = (data: TrailerSchemaType) => {
    onSuccessProp(data)
  }

  return (
    <AddEntityForm dataTest='add-new-trailer-form'>
      {/* Motor Carrier ID */}
      <ControlledHiddenInput
        control={control}
        name='motorCarrierId'
        defaultValue={defaultValuesProp?.motorCarrierId}
      />
      {/* Trailer Number */}
      <div className='mb4'>
        <Controller
          name='trailerNumber'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
              onChange(e.target.value)
            }

            const blurHandler = () => {
              onBlur()
            }

            return (
              <TextInput
                label='Trailer Number'
                id='trailerNumber'
                value={value}
                placeholder='Enter trailer #'
                onChange={changeHandler}
                large
                autoFocus
                required
                onBlur={blurHandler}
                validationMessage={errors?.trailerNumber?.message}
                invalid={!!errors?.trailerNumber?.message}
                dataTest='trailer-number-input'
              />
            )
          }}
        />
      </div>
      {/* Employer */}
      <div className='mb5'>
        <Controller
          name='motorCarrier'
          control={control}
          render={({ onChange, value }) => {
            const changeHandler = ({ value: mcId }: ISelectOption<number>) => {
              const matchingCarrier = carriers.find(c => c.id === mcId)
              if (matchingCarrier) {
                onChange({
                  id: matchingCarrier?.id,
                  name: matchingCarrier.name,
                })
                setValue('motorCarrierId', matchingCarrier.id)
              }
            }

            return (
              <CarrierSelect
                large
                label='Carrier'
                required
                fetchList={!carriersOptions?.length}
                motorCarrierId={value?.id}
                onChange={changeHandler}
                validationMessage={errors?.motorCarrierId?.message}
                invalid={!!errors?.motorCarrierId?.message}
                disabled={disabledFields.includes('motorCarrierId')}
              />
            )
          }}
        />
      </div>
      <AddEntityFormFooter>
        <Button
          color='tertiary'
          size='large'
          type='button'
          onClick={onCancelProp}
          data-test='cancel-new-trailer'>
          Cancel
        </Button>
        <Button
          color='primary'
          size='large'
          type='submit'
          disabled={isLoadingProp}
          data-test='save-new-trailer'
          onClick={handleSubmit(submitHandler)}>
          {primaryButtonTextProp}
        </Button>
      </AddEntityFormFooter>
    </AddEntityForm>
  )
}
